import React, { useState, useEffect } from 'react';
import { Stethoscope, Home, Brain, HeartPulse, Activity, UserCheck, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const careTypes = [
  { key: 'nursing_care', label: 'Nursing Care', icon: Stethoscope },
  { key: 'residential_care', label: 'Residential Care', icon: Home },
  { key: 'dementia_nursing', label: 'Dementia Nursing', icon: Brain },
  { key: 'dementia_residential', label: 'Dementia Residential', icon: UserCheck },
  { key: 'mental_health_nursing', label: 'Mental Health Nursing', icon: HeartPulse },
  { key: 'bariatric_nursing', label: 'Bariatric Nursing', icon: Activity },
];

const AvailabilitySection = () => {
  const [availability, setAvailability] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAvailability();
  }, []);

  const fetchAvailability = async () => {
    try {
      const response = await axios.get(`${API}/availability`);
      setAvailability(response.data);
    } catch (error) {
      console.error('Error fetching availability:', error);
    } finally {
      setLoading(false);
    }
  };

  const scrollToContact = () => {
    const element = document.querySelector('#contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section
      id="availability"
      className="py-20 md:py-32 bg-slate-50"
      data-testid="availability-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#c9a227]/10 rounded-full text-[#c9a227] text-sm font-medium mb-6">
            Bed Availability
          </div>
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1e3a5f] mb-6"
           
          >
            Current Availability
          </h2>
          <p className="text-slate-600 text-lg">
            Enquiries are welcome for all our care services. Contact us to discuss your requirements.
          </p>
        </div>

        {/* Availability Cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {careTypes.map((type) => {
            const IconComponent = type.icon;
            const data = availability?.[type.key];
            const isAvailable = data?.available;
            const beds = data?.beds || 0;

            return (
              <div
                key={type.key}
                className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 hover:shadow-lg transition-all duration-300"
                data-testid={`availability-${type.key}`}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 rounded-xl bg-[#1e3a5f]/10 flex items-center justify-center">
                    <IconComponent className="w-6 h-6 text-[#1e3a5f]" />
                  </div>
                  {loading ? (
                    <Loader2 className="w-5 h-5 text-slate-400 animate-spin" />
                  ) : isAvailable ? (
                    <CheckCircle className="w-6 h-6 text-emerald-500" />
                  ) : (
                    <AlertCircle className="w-6 h-6 text-amber-500" />
                  )}
                </div>
                <h3 className="font-semibold text-[#1e3a5f] mb-2">{type.label}</h3>
                {!loading && (
                  <div className={`text-sm ${isAvailable ? 'text-emerald-600' : 'text-amber-600'}`}>
                    {isAvailable ? `${beds} beds available` : 'Enquire for availability'}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* CTA */}
        <div className="text-center">
          <Button
            onClick={scrollToContact}
            className="bg-[#c9a227] text-[#1e3a5f] hover:bg-[#d4b03a] rounded-full px-8 py-6 text-lg font-medium shadow-lg hover:shadow-xl transition-all duration-300"
            data-testid="check-availability-btn"
          >
            Check Availability Now
          </Button>
        </div>
      </div>
    </section>
  );
};

export default AvailabilitySection;
